import * as Sentry from '@sentry/react';
import { uiEvent, UiEventProps } from './analytics-events';
import { useCallback, useLayoutEffect, useRef } from 'react';
import { TAG_EMPTY_VALUE } from '../sentry';
import { LOG_UI_TRACKING_EVENTS_KEY } from '@upheal/utils/debug-mode/constants';
import delay from 'delay';
import { safeConsole } from '@upheal/utils/safeConsole';
import logger from '@upheal/utils/logger';
import { UiEventAction, UiEventName } from './analytics-types';
import posthog from 'posthog-js';

export const uiEventsHistory = {
    _list: [] as UiEventProps[],
    add: (uiEvent: UiEventProps) => {
        uiEventsHistory._list.push(uiEvent);
    },
    getAll: (): ReadonlyArray<UiEventProps> => {
        return uiEventsHistory._list;
    },
    clear: () => {
        uiEventsHistory._list = [];
    },
};

const uiEventContext = {
    clientId: undefined as string | undefined,
    sessionId: undefined as string | undefined,
    recordingId: undefined as string | undefined,
    tags: [] as Array<string>,
    streamRecordingId: undefined as string | undefined,
    vsrMode: undefined as string | undefined,
    uphealVideoCallId: undefined as string | undefined,
    isDemoContract: undefined as boolean | undefined,
};

const shouldLogToConsole = () =>
    localStorage.getItem(LOG_UI_TRACKING_EVENTS_KEY) === 'true';

export const trackUiEvent = (
    action: UiEventAction,
    uiEventName: UiEventName,
    location?: string,
    property?: string,
    extraProperties?: Record<string, unknown>,
) => {
    const eventProps = {
        uiEventName,
        location,
        property,
        action,
        ...uiEventContext,
        extraProperties,
    };

    if (shouldLogToConsole()) {
        safeConsole.__dangerouslyAllowedInProduction.info(
            'trackUiEvent',
            eventProps,
        );
    }

    uiEventsHistory.add(eventProps);

    // snowplow tracking
    uiEvent(eventProps);

    // posthog tracking
    posthog.capture(uiEventName, {
        location,
        property,
        action,
        ...uiEventContext,
        ...extraProperties,
    });

    // https://docs.sentry.io/platforms/javascript/enriching-events/breadcrumbs/#manual-breadcrumbs
    const messageAttributes = [
        location && `location: '${location}'`,
        property && `property: '${property}'`,
    ]
        .filter(Boolean)
        .join(', ');

    const category = `ui.${action}`;
    const message = `${uiEventName}${
        messageAttributes ? ` [${messageAttributes}]` : ''
    }`;

    Sentry.addBreadcrumb({
        type: 'user',
        category,
        level: 'info',
        message,
    });

    logger.info(`[${category}] ${message}`, {
        uiEventProps: eventProps,
    });
};

const DATA_UI_EVENT_NAME = 'data-ui-event-name';
const DATA_UI_LOCATION = 'data-ui-location';
const DATA_UI_PROPERTY = 'data-ui-property';
const DATA_UI_ACTION = 'data-ui-action';
export const DATA_UI_TRACKING = 'data-ui-tracking';

/**
 * @remarks
 * Experimental API
 * Should be used only on native html elements or React components that pass props to native elements
 *
 * @todo
 * cosider writing a lint rule to enforce 👆
 */
export const uiEventDataAttrs = (
    uiEventName: UiEventName,
    location: string | undefined = undefined,
    property: string | undefined = undefined,
) => {
    return {
        [DATA_UI_EVENT_NAME]: uiEventName,
        [DATA_UI_LOCATION]: location,
        [DATA_UI_PROPERTY]: property,
        [DATA_UI_ACTION]: 'Click', // TODO: other actions
        [DATA_UI_TRACKING]: 'true',
    } as const;
};

export const useTrackUiEventFromClickEvent = () => {
    const unrecognizedClickEvent = useRef<React.MouseEvent | null>(null);

    const findUiEventPropsFromDataAttrs = useCallback(
        (ev: React.MouseEvent) => {
            for (const el of ev.nativeEvent.composedPath()) {
                if (!(el instanceof HTMLElement)) {
                    continue;
                }

                const uiEventName = el.getAttribute(
                    DATA_UI_EVENT_NAME,
                ) as UiEventName | null;
                const location = el.getAttribute(DATA_UI_LOCATION) ?? undefined;
                const property = el.getAttribute(DATA_UI_PROPERTY) ?? undefined;
                const action = (el.getAttribute(DATA_UI_ACTION) ??
                    'Unknown') as UiEventAction;

                if (uiEventName) {
                    return [action, uiEventName, location, property] as const;
                }
            }
        },
        [],
    );

    const onCapturePhaseClick = useCallback(
        (ev: React.MouseEvent) => {
            if (!ev.isTrusted || ev.type !== 'click') {
                return;
            }

            uiEventsHistory.clear();

            const uiEventProps = findUiEventPropsFromDataAttrs(ev);

            // We either found a ui event described by `uiEventDataAttrs`...
            if (uiEventProps) {
                trackUiEvent(...uiEventProps);
                return;
            }

            // ...or we found a click on an element with `cursor: pointer` style. This can be still tracked manually by `trackUiEvent` so we just store the event for now and decide in the bubble phase if we want to track it.
            if (
                ev.target instanceof Element &&
                window.getComputedStyle?.(ev.target).cursor === 'pointer'
            ) {
                unrecognizedClickEvent.current = ev;
                ev.persist();
            }
        },
        [findUiEventPropsFromDataAttrs],
    );

    const onBubblePhaseClick = useCallback(async (ev: React.MouseEvent) => {
        if (!ev.isTrusted || ev.type !== 'click') {
            return;
        }

        // let's delay by a bit because some event handlers might be async
        ev.persist();
        await delay(50);

        // If we found a click on an element with `cursor: pointer` style in the capture phase, and no ui event was tracked, we show warning in the console
        if (
            unrecognizedClickEvent.current?.nativeEvent === ev.nativeEvent &&
            uiEventsHistory.getAll().length === 0 &&
            shouldLogToConsole()
        ) {
            safeConsole.__dangerouslyAllowedInProduction.warn(
                '‼️ Missing UI event',
            );
        }
    }, []);

    return {
        onClickCapture: onCapturePhaseClick,
        onClick: onBubblePhaseClick,
    };
};

export const useSetUiEventContext = ({
    clientId,
    sessionId,
    recordingId,
    streamRecordingId,
    vsrMode,
    vsrExperiment,
    uphealVideoCallId,
    isDemoContract,
}: {
    clientId?: string;
    sessionId?: string;
    recordingId?: string;
    streamRecordingId?: string;
    vsrMode?: string;
    vsrExperiment?: string;
    uphealVideoCallId?: string;
    isDemoContract?: boolean;
}) => {
    useLayoutEffect(() => {
        if (clientId) {
            uiEventContext.clientId = clientId;
            return () => {
                uiEventContext.clientId = undefined;
            };
        }
    }, [clientId]);

    useLayoutEffect(() => {
        if (sessionId) {
            uiEventContext.sessionId = sessionId;
            return () => {
                uiEventContext.sessionId = undefined;
            };
        }
    }, [sessionId]);

    useLayoutEffect(() => {
        if (recordingId) {
            uiEventContext.recordingId = recordingId;
            return () => {
                uiEventContext.recordingId = undefined;
            };
        }
    }, [recordingId]);

    useLayoutEffect(() => {
        const encodedTag = `streamRecordingId:${streamRecordingId}`;

        if (streamRecordingId && !uiEventContext.tags.includes(encodedTag)) {
            uiEventContext.tags.push(encodedTag);
        }

        return () => {
            uiEventContext.tags = uiEventContext.tags.filter(
                (tag) => tag !== encodedTag,
            );
        };
    }, [streamRecordingId]);

    useLayoutEffect(() => {
        const encodedTag = `vsrMode:${vsrMode}`;

        if (vsrMode && !uiEventContext.tags.includes(encodedTag)) {
            uiEventContext.tags.push(encodedTag);
        }

        return () => {
            uiEventContext.tags = uiEventContext.tags.filter(
                (tag) => tag !== encodedTag,
            );
        };
    }, [vsrMode]);

    useLayoutEffect(() => {
        const encodedTag = `vsrExperiment:${vsrExperiment}`;

        if (vsrExperiment && !uiEventContext.tags.includes(vsrExperiment)) {
            uiEventContext.tags.push(encodedTag);
        }

        return () => {
            uiEventContext.tags = uiEventContext.tags.filter(
                (tag) => tag !== encodedTag,
            );
        };
    }, [vsrExperiment]);

    useLayoutEffect(() => {
        if (uphealVideoCallId) {
            uiEventContext.uphealVideoCallId = uphealVideoCallId;
            return () => {
                uiEventContext.uphealVideoCallId = undefined;
            };
        }
    }, [uphealVideoCallId]);

    useLayoutEffect(() => {
        if (isDemoContract !== undefined) {
            uiEventContext.isDemoContract = isDemoContract;
            return () => {
                uiEventContext.isDemoContract = undefined;
            };
        }
    }, [isDemoContract]);

    const refreshSentryTags = () => {
        Sentry.setTags({
            client_id: uiEventContext.clientId ?? TAG_EMPTY_VALUE,
            session_id: uiEventContext.sessionId ?? TAG_EMPTY_VALUE,
            recording_id: uiEventContext.recordingId ?? TAG_EMPTY_VALUE,
            upheal_video_call_id:
                uiEventContext.uphealVideoCallId ?? TAG_EMPTY_VALUE,
            ...Object.fromEntries(
                uiEventContext.tags.map((tag) => tag.split(':')),
            ),
        });
    };

    useLayoutEffect(() => {
        refreshSentryTags();
        return () => {
            refreshSentryTags();
        };
    }, [
        clientId,
        sessionId,
        recordingId,
        streamRecordingId,
        vsrMode,
        vsrExperiment,
        uphealVideoCallId,
    ]);
};

logger.use(async (log) => ({
    ...log,
    uiEventContext,
}));
